import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { redirectToCheckoutPremium } from '../lib/stripe';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  CheckCircle,
  Crown,
  Zap,
  Shield,
  FileText,
  Mail,
  Target
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../hooks/useCurrency';

const PricingPage = () => {
  const { t } = useTranslation();
  const { user, isPremium } = useAuth();
  const currency = useCurrency();
  const navigate = useNavigate();

  const handleUpgrade = async () => {
    if (!user) {
      navigate('/inscription');
      return;
    }
    try {
      await redirectToCheckoutPremium();
    } catch (err) {
      alert(err.response?.data?.detail || t('pricing.error_redirect'));
    }
  };

  const tableRows = [
    { feature: t('pricing.table_analyses'), free: '1', unit: t('pricing.table_unit_analyses'), premium: t('pricing.unlimited') },
    { feature: t('pricing.table_score'), free: true, unit: true, premium: true },
    { feature: t('pricing.table_preview'), free: true, unit: true, premium: true },
    { feature: t('pricing.table_detailed'), free: false, unit: true, premium: true },
    { feature: t('pricing.table_keywords'), free: false, unit: true, premium: true },
    { feature: t('pricing.table_suggestions'), free: false, unit: true, premium: true },
    { feature: t('pricing.table_cv'), free: false, unit: true, premium: true },
    { feature: t('pricing.table_letter'), free: false, unit: true, premium: true },
    { feature: t('pricing.table_export'), free: false, unit: true, premium: true },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />

      <div className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 mb-4">
              <Zap className="h-4 w-4" />
              {t('pricing.tag')}
            </span>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-bold text-zinc-900 mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="pricing-title"
            >
              {t('pricing.title')}
            </h1>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">

            {/* FREE */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-8" data-testid="free-plan">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-zinc-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t('pricing.free_title')}
                </h2>
                <p className="text-zinc-500 text-sm">{t('pricing.free_sub')}</p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-zinc-900">{currency.format(0)}</span>
              </div>

              <ul className="space-y-3 mb-8 text-sm">
                {['free_f1', 'free_f2', 'free_f3', 'free_f4'].map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-zinc-600">{t(`pricing.${key}`)}</span>
                  </li>
                ))}
              </ul>

              <Link to={user ? "/tableau-de-bord" : "/inscription"}>
                <Button variant="outline" className="w-full border-zinc-300 text-zinc-700 py-3 rounded-xl">
                  {user ? t('pricing.free_cta_logged') : t('pricing.free_cta')}
                </Button>
              </Link>
            </div>

            {/* PAY-PER-USE - HIGHLIGHTED */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white relative transform md:-translate-y-4 shadow-xl shadow-blue-600/30" data-testid="payperuse-plan">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-zinc-900 text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1">
                <Zap className="h-3 w-3" />
                {t('pricing.single_tag')}
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t('pricing.single_title')}
                </h2>
                <p className="text-blue-200 text-sm">{t('pricing.single_sub')}</p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold">{currency.format(currency.single)}</span>
                <span className="text-blue-200 ml-2">{t('pricing.unique')}</span>
              </div>

              <ul className="space-y-3 mb-8 text-sm">
                {['instant_unlock', 'all_ats', 'suggestions', 'cv_generated', 'cover_letter'].map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-blue-100">{t(`pricing.${key}`)}</span>
                  </li>
                ))}
              </ul>

              <Link to={user ? "/tableau-de-bord" : "/inscription"}>
                <Button className="w-full bg-white hover:bg-zinc-100 text-blue-700 font-semibold py-3 rounded-xl">
                  <Zap className="h-4 w-4 mr-2" />
                  {t('pricing.single_cta')}
                </Button>
              </Link>
              <p className="text-xs text-blue-200 mt-3 text-center">
                {t('pricing.single_note')}
              </p>
            </div>

            {/* PREMIUM SUBSCRIPTION */}
            <div className="bg-white border-2 border-amber-300 rounded-2xl p-8 relative" data-testid="premium-plan">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1">
                <Crown className="h-3 w-3" />
                {t('pricing.premium_tag')}
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-semibold text-zinc-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t('pricing.premium_title')}
                </h2>
                <p className="text-zinc-500 text-sm">{t('pricing.premium_sub')}</p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-zinc-900">{currency.format(currency.premium)}</span>
                <span className="text-zinc-500 ml-2">{t('pricing.per_month')}</span>
              </div>

              <ul className="space-y-3 mb-8 text-sm">
                {['unlimited_analyses', 'all_features', 'unlimited_cv', 'unlimited_letters', 'cancel_anytime'].map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <span className="text-zinc-600">{t(`pricing.${key}`)}</span>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-zinc-500 mb-4 text-center">
                {t('pricing.premium_profitable')}
              </p>

              {isPremium ? (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl"
                  disabled
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {t('pricing.premium_you')}
                </Button>
              ) : (
                <Button
                  onClick={handleUpgrade}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-xl shadow-lg shadow-amber-500/20"
                  data-testid="upgrade-button"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {t('pricing.premium_cta')}
                </Button>
              )}
            </div>
          </div>

          {/* Comparison Table */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-2xl font-semibold text-zinc-900 text-center mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t('pricing.comparison_title')}
            </h2>

            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left py-4 px-6 text-sm font-semibold text-zinc-700">{t('pricing.feature_col')}</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-zinc-700">{t('pricing.free_title')}</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-blue-600 bg-blue-50">{t('pricing.single_title')}</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-amber-600">{t('pricing.premium_title')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => (
                    <tr key={i} className="border-b border-zinc-100 last:border-0">
                      <td className="py-4 px-6 text-sm text-zinc-700">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {typeof row.free === 'boolean' ? (
                          row.free ? <CheckCircle className="h-5 w-5 text-green-500 mx-auto" /> : <span className="text-zinc-300">—</span>
                        ) : (
                          <span className="text-sm text-zinc-600">{row.free}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center bg-blue-50/50">
                        {typeof row.unit === 'boolean' ? (
                          row.unit ? <CheckCircle className="h-5 w-5 text-blue-500 mx-auto" /> : <span className="text-zinc-300">—</span>
                        ) : (
                          <span className="text-sm text-blue-600 font-medium">{row.unit}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {typeof row.premium === 'boolean' ? (
                          row.premium ? <CheckCircle className="h-5 w-5 text-amber-500 mx-auto" /> : <span className="text-zinc-300">—</span>
                        ) : (
                          <span className="text-sm text-amber-600 font-medium">{row.premium}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-zinc-500 text-center py-3 border-t border-zinc-100">
                {t('pricing.table_note', { price: currency.format(currency.single) })}
              </p>
            </div>
          </div>

          {/* What's included */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-2xl font-semibold text-zinc-900 text-center mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t('pricing.included_title')}
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Target className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2">{t('pricing.included_1_title')}</h3>
                <p className="text-sm text-zinc-600">{t('pricing.included_1_body')}</p>
              </div>

              <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2">{t('pricing.included_2_title')}</h3>
                <p className="text-sm text-zinc-600">{t('pricing.included_2_body')}</p>
              </div>

              <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2">{t('pricing.included_3_title')}</h3>
                <p className="text-sm text-zinc-600">{t('pricing.included_3_body')}</p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto">
            <h2
              className="text-2xl font-semibold text-zinc-900 text-center mb-8"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t('pricing.faq_title')}
            </h2>

            <div className="space-y-4">
              {['pfaq1', 'pfaq2', 'pfaq3', 'pfaq4'].map((key) => (
                <div key={key} className="bg-white border border-zinc-200 rounded-xl p-6">
                  <h3 className="font-semibold text-zinc-900 mb-2">{t(`pricing.${key}_q`)}</h3>
                  <p className="text-zinc-600 text-sm">
                    {t(`pricing.${key}_a`, {
                      singlePrice: currency.format(currency.single),
                      premiumPrice: currency.format(currency.premium),
                    })}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Trust */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm">
              <Shield className="h-4 w-4" />
              <span>{t('dashboard.trust_badge')}</span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PricingPage;
